// @ts-ignore
import { find, isEmpty, findKey } from 'underscore';
// @ts-ignore
import { CivilDate as CivilDateClass } from 'tc39-proposal-temporal';
import {
  compareCivilDate,
  getCivilDateToday,
  isRangeValid,
  getMinDate, getMaxDate,
  getCivilDateMonthStart,
  getCivilDateMonthEnd,
  isDateAvailable,
  isDateInvalid,
  getDaysOfARange,
  getThisSunday,
  getLastSunday,
  getNextSunday,
  getNextNextSunday,
  getThisSaturday,
  getNextSaturday,
  getNextNextSaturday,
  getLastMonday,
  getThisMonday,
  getNextMonday,
  getMonthsOfARange,
  ComparedOptions,
  RANGES,
// @ts-ignore
} from '@bingads-webui-universal/temporal-utitlities';

interface CivilDate {
  year: any;
  month: any;
  day: any;
  plus: (arg0: {
    years?: number; months?: number; days?: number;
  }) => CivilDate;
}

interface RangeDate {
  end: CivilDate;
  start: CivilDate;
}

interface RangeType {
  key: string;
  range: RangeDate;
  rangeId: number;
  text: string;
}

interface NextPrevDate {
  next: RangeDate | null;
  previous: RangeDate | null;
}

export function getRangeString(rangeId: number) {
  const result = findKey(RANGES, (v) => v === rangeId);
  return result || rangeId;
}

function getTextWithRangeIndex(
  i18n: any, rangeIndex: number,
  {
    text, defaultValue, daysAgo, defaultRange = () => false,
  }
  : { text?: string, defaultValue: string, daysAgo?: string, defaultRange?: () => boolean }
): string {
  if (i18n == null || i18n.getString == null) {
    return '';
  }
  if (rangeIndex == null || rangeIndex === 0 || daysAgo == null || defaultRange()) {
    if (!isEmpty(text) && text != null) {
      return text;
    }
    return i18n.getString(defaultValue);
  }
  return i18n.getString(daysAgo, { index: rangeIndex });
}

function plusRangeDate(date: RangeDate, distance: {
  start: {
    years?: number; months?: number; days?: number;
  },
  end: {
    years?: number; months?: number; days?: number;
  }
}) : RangeDate {
  const start = date?.start;
  const end = date?.end;
  if (isDateInvalid(start) || isDateInvalid(end) || start.plus == null || end.plus == null) {
    return {
      start: getCivilDateToday(),
      end: getCivilDateToday(),
    };
  }
  return {
    start: start.plus(distance.start),
    end: end.plus(distance.end),
  };
}

function getNextPreviousDateHelper(newDate: RangeDate, minDate: CivilDate, maxDate: CivilDate) {
  if (!isDateAvailable(newDate.start, minDate, maxDate) && !isDateAvailable(newDate.end, minDate, maxDate)) {
    return null;
  } else if (!isDateAvailable(newDate.start, minDate, maxDate)) {
    return {
      start: minDate,
      end: newDate.end,
    };
  } else if (!isDateAvailable(newDate.end, minDate, maxDate)) {
    return {
      start: newDate.start,
      end: maxDate,
    };
  }
  return newDate;
}

function getFixedNextPreviousDay(selectedRange: RangeDate, daysCount: number): { next: RangeDate, previous: RangeDate } {
  return {
    next: { start: selectedRange.end.plus({ days: 1 }), end: selectedRange.end.plus({ days: daysCount }) },
    previous: { start: selectedRange.start.plus({ days: -daysCount }), end: selectedRange.start.plus({ days: -1 }) },
  };
}

function getNthPeriodOfFixedNDays(rangeIndex: number, rangeDays: number, minDate: CivilDate, maxDate: CivilDate, includeToday = false) {
  const endDate = maxDate.plus({ days: -(includeToday === false ? 1 : 0) });
  const daysInRange = getDaysOfARange({ start: minDate, end: endDate });
  const numberOfRanges = Math.ceil(daysInRange / rangeDays);
  const partialDays = daysInRange % rangeDays === 0 ? rangeDays : daysInRange % rangeDays;
  const maxIndex = numberOfRanges - 1;
  if (rangeIndex >= maxIndex) {
    return {
      rangeIndex: maxIndex,
      range: {
        start: minDate,
        end: minDate.plus({ days: partialDays - 1 }),
      },
    };
  }
  return {
    rangeIndex,
    range: {
      start: endDate.plus({ days: -(rangeIndex * rangeDays) - (rangeDays - 1) }),
      end: endDate.plus({ days: -rangeIndex * rangeDays }),
    },
  };
}

export function getNthPeriodWithRangeType(rangeType: RangeType, rangeIndex: number, minDate: CivilDate, maxDate: CivilDate, rangeDays: number | undefined) {
  if (rangeType == null || rangeIndex == null ||
    isDateInvalid(minDate) || isDateInvalid(maxDate) || compareCivilDate(minDate, maxDate) > 0) {
    // fallback to today,
    return {
      rangeIndex: 0,
      range: {
        start: getCivilDateToday(),
        end: getCivilDateToday(),
      },
    };
  }

  const {
    rangeId,
  } = rangeType;
  let cRangeIndex = rangeIndex < 0 ? 0 : rangeIndex;
  if (rangeId === RANGES.Today || rangeId === RANGES.Yesterday) {
    const maxRangeIndex = getDaysOfARange({ start: minDate, end: maxDate }) - 1;
    if (cRangeIndex > maxRangeIndex) {
      cRangeIndex = maxRangeIndex;
    }
    return {
      rangeIndex: cRangeIndex,
      range: {
        start: maxDate.plus({ days: -cRangeIndex }),
        end: maxDate.plus({ days: -cRangeIndex }),
      },
    };
  } else if (rangeId === RANGES.ThisWeek || rangeId === RANGES.LastWeek) {
    // from Sunday to Saturday
    const maxSunday = getThisSunday(maxDate);
    const maxRange = {
      start: getMaxDate(minDate, maxSunday),
      end: maxDate,
    };
    const minSaturday = getNextSaturday(minDate, true);
    const minRange = {
      start: minDate,
      end: getMinDate(minSaturday, maxDate),
    };
    let totalWeekCount = 0; // Without first week
    if (compareCivilDate(maxSunday, minDate) > 0) {
      // we have a range
      const maxSaturday = maxSunday.plus({ days: -1 });
      const minSunday = getNextSunday(minDate, true);
      // This code calculates the number of weeks that are between the two dates.
      // The number of weeks is calculated by dividing the number of days by 7.
      // The number of weeks is then added to the total week count.
      if (compareCivilDate(minSunday, maxSaturday) <= 0) {
        totalWeekCount += (getDaysOfARange({ start: minSunday, end: maxSaturday })) / 7;
      }

      // if the range is not a multiple of 7, we need to add 1
      const daysToMaxSaturday = getDaysOfARange({ start: minDate, end: maxSaturday });
      if (daysToMaxSaturday % 7 !== 0) {
        totalWeekCount += 1;
      }
    }
    let targetRange;
    if (cRangeIndex >= totalWeekCount) {
      cRangeIndex = totalWeekCount;
      targetRange = minRange;
    } else if (cRangeIndex === 0) {
      targetRange = maxRange;
    } else {
      targetRange = {
        start: maxSunday.plus({ days: -7 * cRangeIndex }),
        end: maxSunday.plus({ days: (-7 * cRangeIndex) + 6 }),
      };
    }
    return {
      rangeIndex: cRangeIndex,
      range: targetRange,
    };
  } else if (rangeId === RANGES.ThisWeekMon || rangeId === RANGES.LastWeekMon) {
    // from Monday to Sunday
    const maxMonday = getThisMonday(maxDate);
    const maxRange = {
      start: getMaxDate(minDate, maxMonday),
      end: maxDate,
    };
    const minSunday = getNextSunday(minDate, true);
    const minRange = {
      start: minDate,
      end: getMinDate(minSunday, maxDate),
    };
    let totalWeekCount = 0; // Without first week
    if (compareCivilDate(maxMonday, minDate) > 0) {
      // we have a range
      const maxSunday = maxMonday.plus({ days: -1 });
      const minMonday = getNextMonday(minDate, true);
      if (compareCivilDate(minMonday, maxSunday) <= 0) {
        totalWeekCount += (getDaysOfARange({ start: minMonday, end: maxSunday })) / 7;
      }
      const daysToMaxSunday = getDaysOfARange({ start: minDate, end: maxSunday });
      if (daysToMaxSunday % 7 !== 0) {
        totalWeekCount += 1;
      }
    }
    let targetRange;
    if (cRangeIndex >= totalWeekCount) {
      cRangeIndex = totalWeekCount;
      targetRange = minRange;
    } else if (cRangeIndex === 0) {
      targetRange = maxRange;
    } else {
      targetRange = {
        start: maxMonday.plus({ days: -7 * cRangeIndex }),
        end: maxMonday.plus({ days: (-7 * cRangeIndex) + 6 }),
      };
    }
    return {
      rangeIndex: cRangeIndex,
      range: targetRange,
    };
  } else if (rangeId === RANGES.ThisMonth || rangeId === RANGES.LastMonth) {
    const totalMonthCount = getMonthsOfARange({ start: minDate, end: maxDate });
    const maxIndex = totalMonthCount - 1;
    const maxDateWithDay1 = new CivilDateClass(maxDate.year, maxDate.month, 1);
    let targetRange;
    if (cRangeIndex === 0) {
      targetRange = {
        start: getCivilDateMonthStart(maxDate),
        end: maxDate,
      };
    } else if (cRangeIndex >= maxIndex) {
      cRangeIndex = maxIndex;
      targetRange = {
        start: minDate,
        end: getCivilDateMonthEnd(minDate),
      };
    } else {
      const targetMonthDate = maxDateWithDay1.plus({ months: -cRangeIndex });
      targetRange = {
        start: getCivilDateMonthStart(targetMonthDate),
        end: getCivilDateMonthEnd(targetMonthDate),
      };
    }
    return {
      rangeIndex: cRangeIndex,
      range: targetRange,
    };
  } else if (rangeId === RANGES.Last7Days) {
    return getNthPeriodOfFixedNDays(cRangeIndex, 7, minDate, maxDate, false);
  } else if (rangeId === RANGES.Last14Days) {
    return getNthPeriodOfFixedNDays(cRangeIndex, 14, minDate, maxDate, false);
  } else if (rangeId === RANGES.Last30Days) {
    return getNthPeriodOfFixedNDays(cRangeIndex, 30, minDate, maxDate, false);
  } else if (rangeId === RANGES.EntireTime) {
    return {
      rangeIndex: 0,
      range: {
        start: minDate,
        end: maxDate,
      },
    };
  }
  // Custom
  if (rangeId === RANGES.Custom && rangeDays) {
    return getNthPeriodOfFixedNDays(cRangeIndex, rangeDays, minDate, maxDate, true);
  }
  // fall back to maxDate
  return {
    rangeIndex: 0,
    range: {
      start: maxDate,
      end: maxDate,
    },
  };
}

export function getRangeIndexWithDefaultType(rangeType: RangeType) {
  if (rangeType == null || rangeType.rangeId == null) {
    return 0;
  }
  const {
    rangeId,
  } = rangeType;
  switch (rangeId) {
    case RANGES.Today:
      return 0;
    case RANGES.Yesterday:
      return 1;
    case RANGES.ThisWeek: // Sunday to Saturday
      return 0;
    case RANGES.LastWeek:
      return 1;
    case RANGES.ThisWeekMon: // Monday to Sunday
      return 0;
    case RANGES.LastWeekMon:
      return 1;
    case RANGES.ThisMonth:
      return 0;
    case RANGES.LastMonth:
      return 1;
    case RANGES.Last7Days:
    case RANGES.Last14Days:
    case RANGES.Last30Days:
    case RANGES.EntireTime:
    case RANGES.Custom:
    default:
      return 0;
  }
}

export function getNextPreviousRangeTypeIndex(rangeType: RangeType, rangeIndex: number) {
  if (rangeType == null || rangeIndex == null) {
    return {
      next: {
        rangeId: RANGES.Today,
        rangeIndex: 0,
      },
      previous: {
        rangeId: RANGES.Yesterday,
        rangeIndex: 1,
      },
    };
  }
  const {
    rangeId,
  } = rangeType;
  const date = {
    next: {
      rangeId,
      rangeIndex: rangeIndex - 1,
    },
    previous: {
      rangeId,
      rangeIndex: rangeIndex + 1,
    },
  };
  switch (rangeId) {
    case RANGES.Today:
      date.previous.rangeId = RANGES.Yesterday;
      break;
    case RANGES.Yesterday:
      if (rangeIndex === 1) {
        date.next.rangeId = RANGES.Today;
      }
      break;
    case RANGES.ThisWeek: // Sunday to Saturday
      date.previous.rangeId = RANGES.LastWeek;
      break;
    case RANGES.LastWeek:
      if (rangeIndex === 1) {
        date.next.rangeId = RANGES.ThisWeek;
      }
      break;
    case RANGES.ThisWeekMon: // Monday to Sunday
      date.previous.rangeId = RANGES.LastWeekMon;
      break;
    case RANGES.LastWeekMon:
      if (rangeIndex === 1) {
        date.next.rangeId = RANGES.ThisWeekMon;
      }
      break;
    case RANGES.ThisMonth:
      date.previous.rangeId = RANGES.LastMonth;
      break;
    case RANGES.LastMonth:
      if (rangeIndex === 1) {
        date.next.rangeId = RANGES.ThisMonth;
      }
      break;
    case RANGES.Last7Days:
      break;
    case RANGES.Last14Days:
      break;
    case RANGES.Last30Days:
      break;
    case RANGES.EntireTime:
      break;
    case RANGES.Custom:
      break;
    default:
      break;
  }
  if (date.next.rangeIndex < 0) {
    date.next.rangeIndex = 0;
    date.previous.rangeIndex = 1;
  }
  return date;
}

export function getXTimesAgoDate(i18n: any, rangeType: RangeType, rangeIndex: number) : string {
  if (rangeType == null || i18n == null || i18n.getString == null) {
    return getTextWithRangeIndex(
      i18n, rangeIndex,
      {
        defaultValue: _TL_('Custom'),
      }
    );
  }
  const {
    rangeId,
    text,
  } = rangeType;
  switch (rangeId) {
    case RANGES.Today:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text, defaultValue: _TL_('Today'),
        }
      );
    case RANGES.Yesterday:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text,
          defaultValue: _TL_('Yesterday'),
          daysAgo: _TL_('{{index}} days ago'),
          defaultRange: () => rangeIndex <= 1,
        }
      );
    case RANGES.ThisWeek:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text, defaultValue: _TL_('This week (Sun - Today)'),
        }
      );
    case RANGES.ThisWeekMon:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text, defaultValue: _TL_('This week (Mon - Today)'),
        }
      );
    case RANGES.LastWeek:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text,
          defaultValue: _TL_('Last week (Sun - Sat)'),
          daysAgo: _TL_('{{index}} weeks ago'),
          defaultRange: () => rangeIndex <= 1,
        }
      );
    case RANGES.LastWeekMon:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text,
          defaultValue: _TL_('Last week (Mon - Sun)'),
          daysAgo: _TL_('{{index}} weeks ago'),
          defaultRange: () => rangeIndex <= 1,
        }
      );
    case RANGES.Last7Days:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text, defaultValue: _TL_('Last 7 days'), daysAgo: _TL_('Custom'),
        }
      );
    case RANGES.Last14Days:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text, defaultValue: _TL_('Last 14 days'), daysAgo: _TL_('Custom'),
        }
      );
    case RANGES.ThisMonth:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text, defaultValue: _TL_('This Month'),
        }
      );
    case RANGES.LastMonth:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text,
          defaultValue: _TL_('Last Month'),
          daysAgo: _TL_('{{index}} months ago'),
          defaultRange: () => rangeIndex <= 1,
        }
      );
    case RANGES.Last30Days:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text, defaultValue: _TL_('Last 30 days'), daysAgo: _TL_('Custom'),
        }
      );
    case RANGES.EntireTime:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text, defaultValue: _TL_('Entire Time'),
        }
      );
    case RANGES.Custom:
    default:
      return getTextWithRangeIndex(
        i18n, rangeIndex,
        {
          text, defaultValue: _TL_('Custom'),
        }
      );
  }
}

export function getNextPreviousDate(
  rangeType: RangeType,
  selectedRange: RangeDate, minDate: CivilDate, maxDate: CivilDate, customRangeDays?: number
): NextPrevDate {
  const date: NextPrevDate = {
    next: null,
    previous: null,
  };
  if (rangeType == null || !isRangeValid(selectedRange)
    || isDateInvalid(minDate) || isDateInvalid(maxDate)) {
    return date;
  }
  const {
    rangeId,
  } = rangeType;
  let fixedNextPreviousDay;
  const startWithDay1 = new CivilDateClass(selectedRange.start.year, selectedRange.start.month, 1);
  const nextMonthDate = startWithDay1.plus({ months: 1 });
  const lastMonthDate = startWithDay1.plus({ months: -1 });
  switch (rangeId) {
    case RANGES.Today:
    case RANGES.Yesterday:
      date.next = getNextPreviousDateHelper(plusRangeDate(selectedRange, { start: { days: 1 }, end: { days: 1 } }), minDate, maxDate);
      date.previous = getNextPreviousDateHelper(plusRangeDate(selectedRange, { start: { days: -1 }, end: { days: -1 } }), minDate, maxDate);
      break;
    case RANGES.ThisWeek: // Sunday to Saturday
    case RANGES.LastWeek:
      date.next = getNextPreviousDateHelper({ start: getNextSunday(selectedRange.start), end: getNextNextSaturday(selectedRange.start, true) }, minDate, maxDate);
      date.previous = getNextPreviousDateHelper({ start: getLastSunday(selectedRange.start), end: getThisSaturday(selectedRange.start, false) }, minDate, maxDate);
      break;
    case RANGES.ThisWeekMon: // Monday to Sunday
    case RANGES.LastWeekMon:
      date.next = getNextPreviousDateHelper({ start: getNextMonday(selectedRange.start), end: getNextNextSunday(selectedRange.start, true) }, minDate, maxDate);
      date.previous = getNextPreviousDateHelper({ start: getLastMonday(selectedRange.start), end: getThisSunday(selectedRange.start, false) }, minDate, maxDate);
      break;
    case RANGES.ThisMonth:
    case RANGES.LastMonth:
      date.next = getNextPreviousDateHelper({ start: getCivilDateMonthStart(nextMonthDate), end: getCivilDateMonthEnd(nextMonthDate) }, minDate, maxDate);
      date.previous = getNextPreviousDateHelper({ start: getCivilDateMonthStart(lastMonthDate), end: getCivilDateMonthEnd(lastMonthDate) }, minDate, maxDate);
      break;
    case RANGES.Last7Days:
      fixedNextPreviousDay = getFixedNextPreviousDay(selectedRange, 7);
      // Not include the maxDate today
      date.next = getNextPreviousDateHelper(fixedNextPreviousDay.next, minDate, maxDate.plus({ days: -1 }));
      date.previous = getNextPreviousDateHelper(fixedNextPreviousDay.previous, minDate, maxDate.plus({ days: -1 }));
      break;
    case RANGES.Last14Days:
      fixedNextPreviousDay = getFixedNextPreviousDay(selectedRange, 14);
      // Not include the maxDate today
      date.next = getNextPreviousDateHelper(fixedNextPreviousDay.next, minDate, maxDate.plus({ days: -1 }));
      date.previous = getNextPreviousDateHelper(fixedNextPreviousDay.previous, minDate, maxDate.plus({ days: -1 }));
      break;
    case RANGES.Last30Days:
      fixedNextPreviousDay = getFixedNextPreviousDay(selectedRange, 30);
      // Not include the maxDate today
      date.next = getNextPreviousDateHelper(fixedNextPreviousDay.next, minDate, maxDate.plus({ days: -1 }));
      date.previous = getNextPreviousDateHelper(fixedNextPreviousDay.previous, minDate, maxDate.plus({ days: -1 }));
      break;
    case RANGES.EntireTime:
      date.next = null;
      date.previous = null;
      break;
    case RANGES.Custom:
      fixedNextPreviousDay = getFixedNextPreviousDay(selectedRange, customRangeDays || (getDaysOfARange(selectedRange)));
      date.next = getNextPreviousDateHelper(fixedNextPreviousDay.next, minDate, maxDate);
      date.previous = getNextPreviousDateHelper(fixedNextPreviousDay.previous, minDate, maxDate);
      break;
    default:
      break;
  }
  return date;
}

const defaultComparedRange = {
  start: undefined,
  end: undefined,
};

// TODO: export all the ComparedType
const ComparedType = {
  Custom: 3,
};

export function getComparedDate({
  range,
  comparedRange,
  rangeType,
  comparedOption,
  minDate, maxDate,
  comparedOn,
  comparedRangeDays,
  next,
} : {
  range: RangeDate | null,
  comparedRange: RangeDate | null,
  rangeType: {
    rangeId: number,
  },
  comparedOption: {
    type: number,
  },
  minDate: CivilDate,
  maxDate: CivilDate,
  comparedOn: boolean,
  comparedRangeDays: number | undefined,
  next: boolean,
}) {
  if (comparedOn === false || comparedOption == null || comparedOption.type == null) {
    return defaultComparedRange;
  }
  const targetOption = find(ComparedOptions, option => option.type === comparedOption.type);
  if (rangeType == null || rangeType.rangeId == null || !isRangeValid(range) || targetOption == null) {
    return defaultComparedRange;
  }
  if (comparedOption.type === ComparedType.Custom) {
    if (isRangeValid(comparedRange) && comparedRange != null) {
      const nextPrevious = getFixedNextPreviousDay(comparedRange, comparedRangeDays || getDaysOfARange(comparedRange));
      const newComparedRange = next ? nextPrevious.next : nextPrevious.previous;
      if (!isDateAvailable(newComparedRange.start, minDate, maxDate) || !isDateAvailable(newComparedRange.end, minDate, maxDate)) {
        return defaultComparedRange;
      }
      return newComparedRange;
    }
    return defaultComparedRange;
  }
  const {
    rangeId,
  } = rangeType;

  const newComparedRange = targetOption.get(range, rangeId);
  if (!isDateAvailable(newComparedRange.start, minDate, maxDate) || !isDateAvailable(newComparedRange.end, minDate, maxDate)) {
    return defaultComparedRange;
  }
  return newComparedRange;
}
